import {
  Booth,
  ContentBlock,
  ContentBlockType,
  Event,
  Festival,
  File,
  Human,
  Sponsor,
} from "@prisma/client";
import { z } from "zod";

export type EventData = Omit<Event, "id" | "createdAt" | "updatedAt"> & {
  categories: string[];
  images: string[];
  humans: string[];
};

// a regex for a url slug
export const slugRegex = /^[a-z0-9-]+$/;

export const ZodEventData: z.ZodSchema<EventData> = z.lazy(() =>
  z.object({
    festivalId: z.string(),
    title: z.string().min(3),
    slug: z.string().regex(slugRegex),
    summary: z.string(),
    from: z.date(),
    to: z.date(),
    embedUrl: z.string().nullable().or(z.string().url()),
    categories: z.array(z.string()),
    humans: z.array(z.string().uuid()),
    images: z.array(z.string().uuid()),
    external: z.boolean(),
    hybrid: z.boolean(),
    location: z.string().nullable(),
    link: z.string().nullable().or(z.string().url()),
    linkText: z.string().nullable(),
  })
);

export function validateEventData(input: any) {
  return ZodEventData.safeParse(input);
}
type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

export type FestivalData = Optional<
  Pick<Festival, "name" | "slug" | "publishedAt">,
  "publishedAt"
>;

export const ZodFestivalData: z.ZodSchema<FestivalData> = z.lazy(() =>
  z.object({
    name: z.string().min(1),
    slug: z.string().regex(slugRegex),
    publishedAt: z.coerce.date().nullable(),
    socialImageId: z.string().nullable(),
    color900: z.string().nullish(),
    color800: z.string().nullish(),
    color700: z.string().nullish(),
    color600: z.string().nullish(),
    color500: z.string().nullish(),
    color400: z.string().nullish(),
    color300: z.string().nullish(),
    color200: z.string().nullish(),
    color100: z.string().nullish(),
    color50: z.string().nullish(),
  })
);

export function validateFestivalData(input: any) {
  return ZodFestivalData.safeParse(input);
}

export type ContentBlockData = Omit<
  ContentBlock,
  "id" | "createdAt" | "updatedAt"
>;

const contentBlogSlugRegex = /^[a-z0-9-\/]+$/;

export const ZodContentBlockData: z.ZodSchema<ContentBlockData> = z.lazy(() =>
  z.object({
    slug: z.string().regex(contentBlogSlugRegex),
    content: z.string().nullable(),
    type: z.enum(["Text", "Html", "Image"]),
    imageId: z.string().nullable(),
  })
);

export function validateContentBlockData(input: any) {
  return ZodContentBlockData.safeParse(input);
}

export type BoothData = Omit<Booth, "id" | "createdAt" | "updatedAt">;

export const ZodBoothData: z.ZodSchema<BoothData> = z.lazy(() =>
  z.object({
    festivalId: z.string().nullable(),
    slug: z.string().regex(slugRegex),
    title: z.string().nonempty(),
    summary: z.string().nullable(),
    text: z.string().nonempty(),
    imageId: z.string().nullable(),
  })
);

export function validateBoothData(input: any) {
  return ZodBoothData.safeParse(input);
}

export type HumanData = Omit<Human, "id" | "createdAt" | "updatedAt">;

export const ZodHumanData: z.ZodSchema<HumanData> = z.lazy(() =>
  z.object({
    festivalId: z.string(),
    name: z.string().nonempty(),
    role: z.string().nonempty(),
    summary: z.string().nonempty(),
    imageId: z.string().nullable(),
  })
);

export function validateHumanData(input: any) {
  return ZodHumanData.safeParse(input);
}
export type SponsorData = Optional<Sponsor, "sortOrder" | "id">;

export const ZodSponsorData: z.ZodSchema<SponsorData> = z.lazy(() =>
  z.object({
    festivalId: z.string(),
    name: z.string().min(1),
    url: z.string().url(),
    sortOrder: z.coerce.number().optional(),
    id: z.string().cuid().optional(),
    imageId: z.string().uuid(),
  })
);

export function validateSponsorData(input: any) {
  return ZodSponsorData.safeParse(input);
}

export type FileData = Omit<File, "id" | "createdAt" | "updatedAt">;

export const ZodFileData: z.ZodSchema<FileData> = z.lazy(() =>
  z.object({
    name: z.string(),
    copyright: z.string(),
    alt: z.string(),
    url: z.string(),
  })
);

export function validateFileData(input: any) {
  return ZodFileData.safeParse(input);
}

const zEnsureString = z.string();
export function ensureString(input: any) {
  return zEnsureString.parse(input);
}

const zContentBlockTypeOrNull = z.nativeEnum(ContentBlockType).nullable();
export function contentBlockTypeOrNull(input: any) {
  return zContentBlockTypeOrNull.parse(input);
}

const zStringOrNull = z.string().nullable();
export function stringOrNull(input: any) {
  return zStringOrNull.parse(input);
}

const zBooleanOrNull = z.boolean().nullable();
export function booleanOrNull(input: any) {
  return zBooleanOrNull.parse(input);
}

const zDate = z.date().nullable();
export function ensureDate(input: any) {
  return zDate.parse(input);
}

const zArrayOfStrings = z.array(z.string());
export function ensureArrayOfStrings(input: any) {
  return zArrayOfStrings.parse(input);
}

const zArrayOfCuids = z.array(z.string().cuid());
export function ensureArrayOfCuids(input: any) {
  return zArrayOfCuids.parse(input);
}

const zArrayOfUuids = z.array(z.string().uuid());
export function ensureArrayOfUuids(input: any) {
  return zArrayOfUuids.parse(input);
}
